"use client";

import React from "react";
import { AnimatePresence, m } from "framer-motion";
import { ArrowUpRight, Rows3 } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { phCapture } from "@/utils/analytics";
import { cn } from "@/lib/utils";
import { trimLine } from "@/components/odds-screen/live/labels";
import { getSportsbookImagePath, getBookDisplayName } from "@/lib/utils/sports-books";
import { StakeLabel, StakeTip, stakeSpoken } from "@/components/odds/stake-tip";
import type { Stake } from "@/lib/book-stake";
import type { BookOddsData, ComparisonTableData } from "./comparison-table-types";
import { americanToDecimal, formatCentsOdds, type OddsFormat } from "@/utils/calculators/odds-utils";

interface ComparisonTableProps {
  data: ComparisonTableData;
  alignWithHeaders?: boolean;
  oddsFormat?: OddsFormat;
  onBookLinkClick?: (book: string) => void;
  renderHistoryTrigger?: (book: BookOddsData) => React.ReactNode;
  renderMovementDelta?: (book: BookOddsData) => React.ReactNode;
  oddsScreenHref?: string | null;
  inserted?: { readonly at: number; readonly node: React.ReactNode } | null;
}

const MISSING_ODDS_TEXT = "-";

function formatOdds(odds: number | null | undefined, oddsFormat: OddsFormat): string {
  if (odds == null || !Number.isFinite(odds)) return MISSING_ODDS_TEXT;
  const n = Number(odds);
  if (oddsFormat === "decimal") {
    const decimal = americanToDecimal(n);
    if (!Number.isFinite(decimal) || decimal <= 1) return MISSING_ODDS_TEXT;
    return decimal.toFixed(2);
  }
  if (oddsFormat === "cents") {
    const decimal = americanToDecimal(n);
    if (!Number.isFinite(decimal) || decimal <= 1) return MISSING_ODDS_TEXT;
    const cents = formatCentsOdds(decimal);
    return cents || MISSING_ODDS_TEXT;
  }
  return n > 0 ? `+${n}` : String(n);
}

const HEADER_OFFSET_PX = 555;

function OddsScreenLinkCard({ href }: { href: string }) {
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener"
      onClick={() => phCapture("odds_screen_prop_link_clicked", { surface: "comparison_table" })}
      className="relative flex shrink-0 flex-col items-center justify-center gap-1 rounded-md px-3 py-2 no-underline transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <ArrowUpRight aria-hidden size={11} strokeWidth={2.5} className="absolute right-1 top-1 text-primary/45" />
      <Rows3 aria-hidden size={18} className="text-primary/80" />
      <span className="text-[11px] font-semibold text-foreground/80">All lines</span>
      <span className="text-[9px] font-medium text-muted-foreground/75">Odds Screen</span>
    </Link>
  );
}

export function ComparisonTable({
  data,
  alignWithHeaders = false,
  oddsFormat = "american",
  onBookLinkClick,
  renderHistoryTrigger,
  renderMovementDelta,
  oddsScreenHref = null,
  inserted = null,
}: ComparisonTableProps) {
  const { selectionLabel, oppositeLabel, books, primaryBook, singleSideOnly } = data;

  const displayBooks = alignWithHeaders
    ? books.filter(b => b.book !== primaryBook)
    : books;

  if (books.length === 0) {
    return (
      <m.div
        initial={{ opacity: 0, y: -4 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        className="flex items-center gap-3 overflow-hidden border-t border-white/[0.08] bg-surface-raised px-4 py-3"
      >
        <span className="text-xs text-foreground/70">No odds available</span>
        {oddsScreenHref && <OddsScreenLinkCard href={oddsScreenHref} />}
      </m.div>
    );
  }

  return (
    <AnimatePresence>
        <m.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative overflow-hidden border-t border-white/[0.08] bg-surface-raised"
        >
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-8 bg-gradient-to-b from-white/[0.03] to-transparent" />

          <div className="flex items-center gap-2.5 px-3 py-2.5">
            {alignWithHeaders && (
              <div
                className="shrink-0 flex items-center justify-end pr-3 border-r border-white/[0.08]"
                style={{ width: HEADER_OFFSET_PX - 32 }}
              >
                <div className="flex flex-col gap-0.5">
                  <span className="flex h-8 items-center text-[11px] font-semibold text-foreground/70">
                    {selectionLabel}
                  </span>
                  <span className="flex h-8 items-center text-[11px] font-semibold text-foreground/70">
                    {oppositeLabel}
                  </span>
                </div>
              </div>
            )}

            {!alignWithHeaders && (
              <div className="shrink-0 flex flex-col pr-2.5 border-r border-white/[0.08]">
                <div className="h-[30px]" />
                <div className="flex flex-col gap-0.5">
                  <span className="flex h-8 items-center justify-end pr-1 text-[11px] font-semibold text-foreground/70" title={selectionLabel}>
                    {selectionLabel}
                  </span>
                  {!singleSideOnly && (
                    <span className="flex h-8 items-center justify-end pr-1 text-[11px] font-semibold text-foreground/70">
                      {oppositeLabel}
                    </span>
                  )}
                </div>
              </div>
            )}

            <div
              className="flex-1 overflow-x-auto scrollbar-custom scrollbar-color-primary"
            >
              <div className={cn("flex gap-1.5", alignWithHeaders && "gap-2")}>
                {displayBooks.map((book, idx) => {
                  const isPrimary = book.book === primaryBook;
                  const hasLineDiff = book.line !== undefined && !isPrimary;
                  const bookDisplayName = getBookDisplayName(book.book);
                  const historyTrigger = renderHistoryTrigger?.(book) ?? null;
                  const tip = book.stake != null;
                  const cardClassName = cn(
                    "relative shrink-0 flex flex-col items-center gap-1.5 rounded-md px-2.5 py-2 no-underline transition-all",
                    isPrimary ? "bg-primary/[0.08]" : book.betLink && "hover:bg-white/[0.06]",
                    isPrimary && book.betLink && "hover:bg-primary/[0.14]",
                    (book.betLink || tip) &&
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40",
                    book.betLink && "cursor-pointer"
                  );
                  const cardContent = (
                    <>
                      <div
                        className={cn(
                          "flex items-center gap-1.5",
                          renderHistoryTrigger && "px-5",
                        )}
                      >
                        <div className="relative shrink-0 rounded-md overflow-hidden">
                          <Image
                            src={getSportsbookImagePath(book.book)}
                            alt={bookDisplayName}
                            width={24}
                            height={24}
                            className="object-contain"
                            unoptimized
                          />
                          {isPrimary && (
                            <div className="absolute -right-0.5 -top-0.5 size-2 rounded-full bg-primary shadow-[0_0_4px_rgba(var(--primary-rgb),0.5)]" />
                          )}
                        </div>
                        {hasLineDiff && (
                          <span className="text-[10px] data-num font-medium tabular-nums text-zinc-500">
                            ALT {trimLine(book.line!)}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-0.5">
                        <OddsChip
                          odds={book.selectionOdds}
                          isBest={book.isBestSelection}
                          isPrimary={isPrimary}
                          altLine={hasLineDiff}
                          stake={book.stake?.stake ?? null}
                          oddsFormat={oddsFormat}
                        />
                        {!singleSideOnly && (
                          <OddsChip
                            odds={book.oppositeOdds}
                            isBest={book.isBestOpposite}
                            isPrimary={isPrimary}
                            altLine={hasLineDiff}
                            muted
                            stake={book.oppositeStake ?? null}
                            oddsFormat={oddsFormat}
                          />
                        )}
                      </div>

                      {isPrimary && renderMovementDelta?.(book)}
                    </>
                  );

                  const sides = [
                    { label: selectionLabel, stake: book.stake?.stake ?? null },
                    { label: oppositeLabel, stake: singleSideOnly ? null : book.oppositeStake ?? null },
                  ];
                  const spoken = sides.map(({ label, stake }) => stake === null ? "" : `, ${label}${stakeSpoken(stake)}`).join("");
                  const card = (
                    <StakeTip quote={book.stake ?? null} format={oddsFormat}>
                      {book.betLink ? (
                        <a
                          href={book.betLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={tip ? undefined : `Place bet at ${bookDisplayName}`}
                          aria-label={`Place bet at ${bookDisplayName}${spoken}`}
                          className={cardClassName}
                          onClick={(event) => {
                            event.stopPropagation();
                            onBookLinkClick?.(book.book);
                          }}
                        >
                          {cardContent}
                        </a>
                      ) : (
                        <div className={cardClassName} tabIndex={tip ? 0 : undefined} aria-label={spoken === "" ? undefined : `${bookDisplayName}${spoken}`}>{cardContent}</div>
                      )}
                    </StakeTip>
                  );

                  const lead = inserted?.at === idx ? inserted.node : null;
                  if (!historyTrigger) {
                    return (
                      <React.Fragment key={`book-${book.book}-${idx}`}>
                        {lead}
                        {card}
                      </React.Fragment>
                    );
                  }
                  return (
                    <React.Fragment key={`book-${book.book}-${idx}`}>
                      {lead}
                      <div className="group/history relative shrink-0">
                        {card}
                        <div className="pointer-events-none absolute left-0.5 top-0.5 z-10">
                          {historyTrigger}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                {inserted !== null && inserted.at >= displayBooks.length && inserted.node}
                {oddsScreenHref && <OddsScreenLinkCard href={oddsScreenHref} />}
              </div>
            </div>
          </div>
        </m.div>
      </AnimatePresence>
      );
}

function OddsChip({
  odds,
  isBest,
  isPrimary,
  muted,
  altLine,
  stake,
  oddsFormat,
}: {
  odds: number | null | undefined;
  isBest?: boolean;
  isPrimary?: boolean;
  muted?: boolean;
  altLine?: boolean;
  stake: Stake | null;
  oddsFormat: OddsFormat;
}) {
  const formatted = formatOdds(odds, oddsFormat);
  const isEmpty = formatted === MISSING_ODDS_TEXT;

  if (isEmpty) {
    return (
      <span className="flex h-8 w-16 items-center justify-center text-[11px] text-foreground/70">
        {MISSING_ODDS_TEXT}
      </span>
    );
  }

  const showPrimaryHighlight = isPrimary && !muted;

  return (
    <span
      className={cn(
        "relative flex h-8 w-16 flex-col items-center justify-center rounded-md text-xs font-bold tabular-nums transition-all",
        isBest && "text-accent-value-strong",
        !isBest && showPrimaryHighlight && "text-primary/80",
        !isBest && !showPrimaryHighlight && cn("text-foreground/90", altLine && "opacity-75"),
      )}
    >
      {isBest && !muted ? <span className="sr-only">Best price. </span> : null}
      <span className="leading-4">{formatted}</span>
      {stake !== null && <StakeLabel stake={stake} />}
    </span>
  );
}
